/**
 * Pure helpers shared by SummaryView and the association tables it renders.
 *
 * Lives in its own (non-component) module so it can be exported without
 * tripping the react-refresh/only-export-components ESLint rule that fires
 * when components and non-component values share a file.
 */

import type { FileSummary, UploadedFileEntry } from '@/lib/csv-import/api';

/**
 * Resolve a human-friendly display name for an entity row using a best-effort
 * waterfall: mapped CRM properties first, then raw CSV columns as a fallback
 * for joins on unmapped fields. Returns null when nothing usable is found —
 * the caller can fall back to "Row N" or similar.
 */
export function getEntityRowDisplayName(
  data: Record<string, unknown>,
  rawData: Record<string, unknown>,
  entityTypeName: string
): string | null {
  const pick = (value: unknown): string | null => {
    if (value === undefined || value === null) {
      return null;
    }
    const str = String(value).trim();
    return str === '' ? null : str;
  };

  const fromMapped = (
    properties: string[],
    join?: (parts: string[]) => string
  ): string | null => {
    const resolved = properties
      .map(p => pick(data[p]))
      .filter((v): v is string => v !== null);
    if (resolved.length === 0) {
      return null;
    }
    return join ? join(resolved) : resolved[0];
  };

  const fromRaw = (...columns: string[]): string | null => {
    for (const col of columns) {
      const value = pick(rawData[col]);
      if (value) {
        return value;
      }
      // Case-insensitive fallback for header casing differences.
      const matchKey = Object.keys(rawData).find(
        k => k.toLowerCase() === col.toLowerCase()
      );
      if (matchKey) {
        const v = pick(rawData[matchKey]);
        if (v) {
          return v;
        }
      }
    }
    return null;
  };

  switch (entityTypeName) {
    case 'ahoy_contact': {
      const fullName = fromMapped(
        ['ahoy_first_name', 'ahoy_last_name'],
        parts => parts.join(' ')
      );
      if (fullName) {
        return fullName;
      }
      return (
        fromMapped(['ahoy_email']) ??
        fromRaw('First Name', 'Last Name', 'Full Name', 'Name', 'Email')
      );
    }
    case 'ahoy_company':
      return (
        fromMapped(['ahoy_name']) ??
        fromMapped(['ahoy_domain']) ??
        fromRaw('Company Name', 'Name', 'Domain', 'Website')
      );
    case 'ahoy_deal':
      return fromMapped(['ahoy_name']) ?? fromRaw('Deal Name', 'Name');
    case 'ahoy_note':
      return (
        fromMapped(['ahoy_title']) ??
        // Truncate long note content for the table cell.
        (() => {
          const content = pick(data.ahoy_content) ?? fromRaw('Note', 'Content');
          return content && content.length > 40
            ? `${content.slice(0, 37)}…`
            : content;
        })()
      );
    default:
      return null;
  }
}

/**
 * Compact row-number hint for the grouped associations table. Renders short
 * lists verbatim ("Row 3", "Rows 3, 7"); for longer lists truncates to the
 * first few + "+N more" so the cell stays readable.
 */
export function formatRowNumbersHint(rows: number[]): string {
  if (rows.length === 0) {
    return '';
  }
  if (rows.length === 1) {
    return `Row ${rows[0]}`;
  }
  if (rows.length <= 4) {
    return `Rows ${rows.join(', ')}`;
  }
  const head = rows.slice(0, 3).join(', ');
  return `Rows ${head} +${rows.length - 3} more`;
}

export interface FileBreakdownType {
  entityTypeName: string;
  /** Records written to the CRM for this type = new_rows + update_rows. */
  recordCount: number;
}

export interface FileBreakdownRow {
  /** Stable React key: `source-<id>` for split uploads, `file-<id>` otherwise. */
  key: string;
  /** User-facing source filename, shown once as the row header. */
  sourceFilename: string;
  types: FileBreakdownType[];
}

/**
 * Group the review payload's virtual files back under their source upload so the
 * "Files" section shows one row per uploaded file with a per-entity-type record
 * count. A multi-type CSV becomes one row with several `types`; a single-type CSV
 * becomes one row with a single `type`. Returns [] when there are no file summaries
 * (the section is then hidden). Order follows `uploadedFiles`.
 */
export function buildFileBreakdown(
  uploadedFiles: UploadedFileEntry[],
  fileSummaries: Record<string, FileSummary> | undefined
): FileBreakdownRow[] {
  if (!fileSummaries) {
    return [];
  }
  const groups = new Map<string, FileBreakdownRow>();
  for (const file of uploadedFiles) {
    // Source-driven imports (e.g. Lightfield) fabricate byte-less virtual entries
    // with no filename purely to reuse this pipeline — skip them so the section
    // doesn't render rows for files a user can't see.
    if (!file.filename) {
      continue;
    }
    const fileId = String(file.file_id);
    // Multi-type CSVs split into one virtual entry per type, each carrying the SAME
    // source filename and source_file_id — group by source_file_id to collapse them
    // into one row. Traditional/legacy single-type entries have no source_file_id
    // and fall back to their own file_id. The virtual entry's own `filename` is
    // already the source filename, so no cross-entry lookup is needed.
    const key =
      file.source_file_id !== undefined
        ? `source-${file.source_file_id}`
        : `file-${fileId}`;
    const sourceFilename = file.filename;
    const summary = fileSummaries[fileId];
    const recordCount = (summary?.new_rows ?? 0) + (summary?.update_rows ?? 0);
    const group = groups.get(key) ?? { key, sourceFilename, types: [] };
    group.types.push({ entityTypeName: file.entity_type_name, recordCount });
    groups.set(key, group);
  }
  return Array.from(groups.values());
}
